import {gsap} from 'gsap';
import {
  getMousePos,
  map,
  lerp,
  type MousePosition,
} from '@backstage-components/base';
import {AnimatableProperties} from './MagicTextComponent';

// track the mouse position
let mousepos: MousePosition = {x: 0, y: 0};

interface DomElement {
  el: Element;
  reveal: HTMLDivElement;
  revealInner: HTMLDivElement;
  revealImage: HTMLDivElement;
}

interface Bounds {
  el: DOMRect;
  reveal: DOMRect;
}

const isTouchDevice = (): boolean =>
  'ontouchstart' in window || navigator.maxTouchPoints > 0;

export class HoverItem {
  private images: string[];
  private DOM: DomElement;
  private moduleContainer: Element;
  private hoverPosition: number;
  private animatableProperties: AnimatableProperties;
  private bounds: Bounds;

  // flag to determine if it is the first render cycle or not
  private firstRAFCycle: boolean;
  // flag for whether or not an image is currently showing/revealed
  private showingImage: boolean;
  private mouseenterFn: () => void;
  private mouseleaveFn: () => void;
  tl: gsap.core.Timeline;
  requestId?: number;

  constructor(
    el: Element,
    hoverPosition: number,
    animatableProperties: AnimatableProperties,
    hoverImages: string[],
    moduleContainer: Element
  ) {
    // update mouse position when moving the mouse
    window.addEventListener('mousemove', (ev) => (mousepos = getMousePos(ev)));
    this.images = hoverImages;
    this.moduleContainer = moduleContainer;
    this.firstRAFCycle = false;
    this.showingImage = false;
    // el is the <em>
    // create the rest of the html structure with `createElement`
    this.DOM = {
      el: el,
      reveal: document.createElement('div'),
      revealInner: document.createElement('div'),
      revealImage: document.createElement('div'),
    };
    // position in the content block
    this.hoverPosition = hoverPosition;
    // hover item properties that will animate as we move the mouse around the hover item
    this.animatableProperties = animatableProperties;
    // create the image structure
    this.layout();
    // initialize some events
    this.initEvents();
  }

  // create the image structure
  // we want to add/append to the hover item the following html:
  // <div class="hover-reveal">
  //   <div class="hover-reveal__inner" style="overflow: hidden;">
  //     <div class="hover-reveal__img" style="background-image: url(pathToImage);">
  //     </div>
  //   </div>
  // </div>
  layout(): void {
    // this is the element that gets its position animated (and perhaps other properties like the rotation etc..)
    this.DOM.reveal.className = 'hover-reveal';
    this.DOM.reveal.style.perspective = '1000px';

    // the next two elements could actually be only one, the image element
    // adding an extra wrapper (revealInner) around the image element with overflow hidden, gives us the possibility to scale the image inside
    this.DOM.revealInner.className = 'hover-reveal__inner';
    this.DOM.revealImage.className = 'hover-reveal__img';

    // get image width and height
    const imgContainer = this.moduleContainer.querySelector('.image-preload');
    if (!imgContainer) return;
    const img = imgContainer.querySelectorAll('img')[this.hoverPosition];
    const width = img?.clientWidth ?? 0;
    const height = img?.clientHeight ?? 0;

    // Set the aspect ratio of the image and set the width
    const aspectRatioPadding = `${(height / width) * 100}%`;
    this.DOM.revealInner.style.paddingTop = aspectRatioPadding;
    this.DOM.reveal.style.width = `${width}px`;

    this.DOM.revealImage.style.backgroundImage = `url(${
      this.images[this.hoverPosition]
    })`;

    this.DOM.revealInner.appendChild(this.DOM.revealImage);
    this.DOM.reveal.appendChild(this.DOM.revealInner);
    this.DOM.el.appendChild(this.DOM.reveal);
  }

  // calculate the position/size of both the hover item and reveal element
  calcBounds(): void {
    if (this.DOM.reveal) {
      this.bounds = {
        el: this.DOM.el.getBoundingClientRect(),
        reveal: this.DOM.reveal.getBoundingClientRect(),
      };
    }
  }
  // bind some events
  initEvents(): void {
    this.mouseenterFn = () => {
      // show the image element
      this.showImage();
      this.showingImage = true;
      this.firstRAFCycle = true;
      // start the render loop animation (rAF)
      this.loopRender();
    };
    this.mouseleaveFn = () => {
      if (this.showingImage) {
        this.showingImage = false;
        // stop the render loop animation (rAF)
        this.stopRendering();
        // hide the image element
        this.hideImage();
      }
    };

    if (isTouchDevice()) {
      this.DOM.el.addEventListener('touchstart', this.mouseenterFn);
      this.DOM.el.addEventListener('touchmove', this.mouseleaveFn);
      this.DOM.el.addEventListener('touchcancel', this.mouseleaveFn);
      document.addEventListener('scroll', this.mouseleaveFn);
    } else {
      this.DOM.el.addEventListener('mouseenter', this.mouseenterFn);
    }

    this.DOM.el.addEventListener('mouseleave', this.mouseleaveFn);
  }
  // show the image element
  showImage(): void {
    // kill any current tweens
    gsap.killTweensOf(this.DOM.revealInner);
    gsap.killTweensOf(this.DOM.revealImage);

    this.tl = gsap
      .timeline({
        onStart: () => {
          // show both image and its parent element
          this.DOM.reveal.style.opacity = this.DOM.revealInner.style.opacity =
            '1';
          // set a high z-index value so image appears on top of other elements
          gsap.set(this.DOM.el, {zIndex: this.images.length});
        },
      })
      // animate the image wrap
      .to(this.DOM.revealInner, {
        duration: 1.2,
        ease: 'Expo.easeOut',
        startAt: {rotationY: -90, scale: 0.7},
        rotationY: 0,
        scale: 1,
      })
      .to(
        this.DOM.revealImage,
        {
          duration: 1.2,
          ease: 'Expo.easeOut',
          startAt: {scale: 1.3, filter: 'blur(8px) brightness(2)'},
          scale: 1,
          filter: 'blur(0px) brightness(1)',
        },
        0
      );
  }
  // hide the image element
  hideImage(): void {
    // kill any current tweens
    gsap.killTweensOf(this.DOM.revealInner);
    gsap.killTweensOf(this.DOM.revealImage);

    this.tl = gsap
      .timeline({
        onStart: () => {
          gsap.set(this.DOM.el, {zIndex: 1});
        },
        onComplete: () => {
          gsap.set(this.DOM.reveal, {opacity: 0});
        },
      })
      .to(this.DOM.revealInner, {
        duration: 0.5,
        ease: 'Expo.easeOut',
        rotationY: 90,
        opacity: 0,
      });
  }
  // start the render loop animation (rAF)
  loopRender(): void {
    if (!this.requestId) {
      this.requestId = requestAnimationFrame(() => this.render());
    }
  }
  // stop the render loop animation (rAF)
  stopRendering(): void {
    if (this.requestId) {
      window.cancelAnimationFrame(this.requestId);
      this.requestId = undefined;
    }
  }
  // translate the item as the mouse moves
  render(): void {
    this.requestId = undefined;

    if (this.firstRAFCycle) {
      // calculate position/sizes the first time
      this.calcBounds();
    }

    // new translation values
    this.animatableProperties.translateX.current =
      Math.abs(mousepos.x - this.bounds.el.left) - this.bounds.reveal.width / 2;
    this.animatableProperties.translateY.current =
      Math.abs(mousepos.y - this.bounds.el.top) - this.bounds.reveal.height / 2;
    // new rotation value
    this.animatableProperties.rotation.current = map(
      Math.abs(mousepos.x - this.bounds.el.left),
      0,
      this.bounds.el.left + this.bounds.el.width,
      -20,
      20
    );

    // set up the interpolated values
    // for the first cycle, both the interpolated values need to be the same so there's no "lerped" animation between the previous and current state
    this.animatableProperties.translateX.previous = this.firstRAFCycle
      ? this.animatableProperties.translateX.current
      : lerp(
          this.animatableProperties.translateX.previous,
          this.animatableProperties.translateX.current,
          this.animatableProperties.translateX.amount
        );
    this.animatableProperties.translateY.previous = this.firstRAFCycle
      ? this.animatableProperties.translateY.current
      : lerp(
          this.animatableProperties.translateY.previous,
          this.animatableProperties.translateY.current,
          this.animatableProperties.translateY.amount
        );
    this.animatableProperties.rotation.previous = this.firstRAFCycle
      ? this.animatableProperties.rotation.current
      : lerp(
          this.animatableProperties.rotation.previous,
          this.animatableProperties.rotation.current,
          this.animatableProperties.rotation.amount
        );

    const width = window.innerWidth > 0 ? window.innerWidth : screen.width;

    // set styles, image is centered on the screen for tablet portrait and below
    if (width <= 768) {
      gsap.set(this.DOM.reveal, {
        x: '-50%',
        y: '-50%',
        rotation: 0,
      });
    } else {
      gsap.set(this.DOM.reveal, {
        x: this.animatableProperties.translateX.previous,
        y: this.animatableProperties.translateY.previous,
        rotation: this.animatableProperties.rotation.previous,
      });
    }

    // loop
    this.firstRAFCycle = false;
    this.loopRender();
  }
}
